import { comparer, reaction } from 'mobx';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import {
  pickSolutionPresences,
  presencesToRender,
} from '../collab/collab-presence';
import { isWorkflowsPath } from '../collab/collab-routes';
import { tableStore } from '../stores/table-store';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import type { SolutionUserPresenceItem } from '../xstate/prostudio.xstate.types';

/**
 * Who else is in this solution, for cell focus, avatars and the publish
 * modal: the collab document's collaborators while the Workflows table is fed
 * by it (PSTUDIO-4451), the Hasura presence otherwise.
 */
export const useSolutionPresences = ():
  | SolutionUserPresenceItem[]
  | undefined => {
  const { pathname } = useLocation();
  const legacy = useProStudioCtxSelector((ctx) => ctx.solutionUserPresences);
  // The page the editor's presence filters compare against (primary nav).
  const presencePagePath = useProStudioCtxSelector(
    (ctx) => ctx.presencePagePath
  );
  const pagePath = presencePagePath ?? pathname;
  const [live, setLive] = useState<SolutionUserPresenceItem[] | null>(null);

  useEffect(
    () =>
      reaction(
        () => {
          const store = tableStore.liveEditingSource
            ? tableStore.liveViewSettings
            : null;
          return store
            ? (pickSolutionPresences(undefined, store, pagePath) ?? null)
            : null;
        },
        setLive,
        // A fresh array every recompute; structural, so an unchanged list
        // does not re-render every row that reads it.
        { fireImmediately: true, equals: comparer.structural }
      ),
    [pagePath]
  );

  // The flag is set from Playwright and never assigned in app code, so this
  // is `false` in prod and dev.
  const testOwned =
    typeof window !== 'undefined' &&
    (window as unknown as { __PS_E2E_FREEZE_PRESENCE__?: boolean })
      .__PS_E2E_FREEZE_PRESENCE__ === true;

  const onWorkflows = isWorkflowsPath(pathname);
  // Memoised: a fresh array on every render would undo the structural
  // comparison above for every row that reads the list.
  return useMemo(
    () => presencesToRender({ legacy, live, testOwned, onWorkflows, pagePath }),
    [legacy, live, testOwned, onWorkflows, pagePath]
  );
};
